import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Recojo from './recojo/Recojo';
import Entrega from './entrega/Entrega';
import Options from './options/Options';
import MyAccount from './account/MyAccount';
import MapSelection from './recojo/MapSelection';
import PrintDeliveries from './analytics/PrintDeliveries';
import Inventory from './inventory/Inventory';
import StoreCommunicationInbox from '../shared/communication/StoreCommunicationInbox';
import SupportCommunicationInbox from '../shared/communication/SupportCommunicationInbox';
import ControlTower from './controltower/ControlTower';
import Analytics from './analytics/Analytics';
import ProviderCatalog from './catalog/ProviderCatalog';
import EntregaProvincia from './entrega/EntregaProvincia';
import ReferralDashboard from './referral/ReferralDashboard';
import MasivoWizard from './masivo/MasivoWizard';
import ProvinciaMasivoWizard from './masivoProvincia/ProvinciaMasivoWizard';
import ExpressDeliveryPage from './express/ExpressDeliveryPage';
import Integrations from '../shared/integrations/Integrations';
import DeliveryIntelligence from './intelligence/DeliveryIntelligence';
import StoreAgencyEntry from './agency/StoreAgencyEntry';

const COMPONENT_DEPTH = {
  Options: 0,
  MyAccount: 1,
  Analytics: 1,
  ControlTower: 1,
  Chat: 1,
  Inventory: 1,
  ProviderCatalog: 1,
  SupportChat: 2,
  Referrers: 2,
  Integrations: 2,
  DeliveryIntelligence: 1,
  AgencyWorkspace: 1,
};

const pageVariants = {
  enter: ({ direction, reduceMotion }) => ({
    x: reduceMotion ? '0%' : direction > 0 ? '100%' : '-100%',
    opacity: 1,
    zIndex: 2,
  }),
  center: {
    x: '0%',
    opacity: 1,
    zIndex: 2,
  },
  exit: ({ direction, reduceMotion }) => ({
    x: reduceMotion ? '0%' : direction > 0 ? '-100%' : '100%',
    opacity: 1,
    zIndex: 1,
  }),
};

function MainUser({ user_id, username, onLogout, email, phone_number, onUpdateUser }) {
  const [activeComponent, setActiveComponentState] = useState('Options');
  const [direction, setDirection] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const activeComponentRef = useRef('Options');
  const reduceMotion = useReducedMotion();

  const setActiveComponent = useCallback((nextValue) => {
    const currentComponent = activeComponentRef.current;
    const nextComponent =
      typeof nextValue === 'function' ? nextValue(currentComponent) : nextValue;

    if (!nextComponent || nextComponent === currentComponent) return;

    const currentDepth = COMPONENT_DEPTH[currentComponent] ?? 1;
    const nextDepth = COMPONENT_DEPTH[nextComponent] ?? 1;

    setDirection(nextDepth < currentDepth ? -1 : 1);
    activeComponentRef.current = nextComponent;
    setActiveComponentState(nextComponent);
  }, []);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user_addresses/${user_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.addresses.length === 0) {
          setActiveComponent('MapSelection');
        } else {
          setAddresses(response.data.addresses);
        }
      } catch (error) {
        console.error('Error fetching user addresses:', error);
      }
    };

    if (addresses && addresses.length === 0) {
      fetchAddresses();
    }
  }, [addresses, setActiveComponent, user_id]);

  function renderActiveComponent() {
    switch (activeComponent) {
      case 'AgencyWorkspace':
        return <StoreAgencyEntry onBack={() => setActiveComponent('Options')} />;
      case 'Recojo':
        return <Recojo setActiveComponent={setActiveComponent} />;
      case 'Entrega':
        return <Entrega userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'EntregaExpress':
        return <ExpressDeliveryPage userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'EntregaProvincia':
        return <EntregaProvincia userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'ControlTower':
        return <ControlTower userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'Inventory':
        return <Inventory userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'Analytics':
        return <Analytics userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'ProviderCatalog':
        return <ProviderCatalog userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'MyAccount':
        return (
          <MyAccount
            onUpdateUser={onUpdateUser}
            userId={user_id}
            username={username}
            phoneNumber={phone_number}
            email={email}
            onLogout={onLogout}
            setActiveComponent={setActiveComponent}
          />
        );
      case 'MapSelection':
        return <MapSelection setActiveComponent={setActiveComponent} userId={user_id} />;
      case 'Print':
        return <PrintDeliveries userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'Chat':
        return <StoreCommunicationInbox userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'SupportChat':
        return (
          <SupportCommunicationInbox
            userId={user_id}
            setActiveComponent={setActiveComponent}
            scope="store"
            backTarget="Options"
          />
        );
      case 'Referrers':
        return (
          <ReferralDashboard
            userId={user_id}
            setActiveComponent={setActiveComponent}
            returnComponent="MyAccount"
          />
        );
      case 'MasivoWizard':
        return <MasivoWizard userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'ProvinciaMasivoWizard':
        return <ProvinciaMasivoWizard userId={user_id} setActiveComponent={setActiveComponent} />;
      case 'Integrations':
        return (
          <Integrations
            userId={user_id}
            setActiveComponent={setActiveComponent}
            returnComponent="MyAccount"
          />
        );
      case 'DeliveryIntelligence':
        return <DeliveryIntelligence setActiveComponent={setActiveComponent} />;
      case 'Options':
      default:
        return (
          <Options
            userId={user_id}
            username={username}
            setActiveComponent={setActiveComponent}
            onLogout={onLogout}
          />
        );
    }
  }

  const motionContext = { direction, reduceMotion };

  return (
    <div className="relative isolate grid min-h-screen overflow-x-hidden bg-slate-50">
      <AnimatePresence initial={false} custom={motionContext} mode="sync">
        <motion.div
          key={activeComponent}
          custom={motionContext}
          variants={pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
          }
          className="relative min-h-screen min-w-0 w-full [grid-area:1/1]"
          style={{ backfaceVisibility: 'hidden' }}
        >
          {renderActiveComponent()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default MainUser;
